/* eslint-disable max-len */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { Col, Collapse, Form, Modal, Row, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import TooltipComponent from 'components/atoms/TooltipComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import { RowTitles } from 'components/molecules/RowTitles';
import ErrModal from 'components/molecules/SpecialniSluzby/ErrModal';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import fileDownload from 'services/fileDownload';
import {
    ETypPrevodu,
    IDetailDLTinput,
    IPozadavkyDLT,
    IPozadavkyPolozkaDLT,
    uePozadavkyDetailDLTApi
} from 'services/specialniSluzby/uePozadavky';
import { ueSouborVystupCSV } from 'services/specialniSluzby/ueSoubor';
import { uePotvrzeniSingleDLT, ueZruseniDLT, ueZruseniSingleDLT } from 'services/specialniSluzby/ueZruseni';

import { ENotificationType } from 'enums/notificationType';
import routePathEnum from 'enums/routePathEnum';
import { EStavPozadavku } from 'enums/specialniSluzby/stavPozadavku';
import { ETypPozadavku } from 'enums/specialniSluzby/typPozadavku';
import { ETypSeznam } from 'enums/specialniSluzby/typSeznam';

import identifikaceOsobySelect from 'data/codebooks/anoNeSelect';
import { typStavZadostiDLTSelect } from 'data/codebooks/specialniSluzby/typStavZadosti';
import { typZpusobPorizeniSelect } from 'data/codebooks/specialniSluzby/typZpusobPorizeni';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';

import NovyPozadavekDLT from './NovyPozadavekDLT';

const rowTitleStyles = {
    maxWidth: '650px',
    fontSize: '16px'
};
export const rowTitleStylesCollapse = {
    maxWidth: '1050px',
    fontSize: '16px'
};

const DetailPozadavkuDLT: React.FC = () => {
    const { Panel } = Collapse;
    const { confirm } = Modal;
    const { aktivniFirma, specialniSluzby, language, NotificationComponent } = useContext(Context);
    const { setLevel } = useProfily();
    const { navigateWorkaround } = useNavigateWorkaround();
    const [form] = Form.useForm();
    const [form2] = Form.useForm();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<IPozadavkyDLT | undefined>(specialniSluzby?.detail as unknown as IPozadavkyDLT);
    const [zmenaPozadavku, setZmenaPozadavku] = useState<'edit' | 'novy' | undefined>();
    const [selectedDetail, setSelectedDetail] = useState<IPozadavkyPolozkaDLT[]>();
    const [pagination, setPagination] = useState<IDetailDLTinput>({
        offset: 0,
        limit: 10,
        order: 'poradi:desc:idpolozky:desc'
    });
    const [showConfirmationModal, setShowConfirmationModal] = useState<'potvrdit' | 'zrusit' | undefined>();
    const [errModalMsg, setErrModalMsg] = useState<string>();
    const isZpetnyPrevod =
        data?.typ === ETypPozadavku.PREVODZPET || specialniSluzby?.detail?.typ === ETypPozadavku.PREVODZPET;

    // nastavenie casovej zony pre tuto page
    useEffect(() => {
        dayjs.tz.setDefault('Europe/Prague');
        return () => {
            dayjs.tz.setDefault();
        };
    }, []);

    const isAfterMezniDatum =
        specialniSluzby?.detail?.meznidatum || data?.meznidatum
            ? dayjs(
                  `${dayjs(specialniSluzby?.detail?.meznidatum || data?.meznidatum).format('YYYY-MM-DD')} ${'16:30'}`
              ) < dayjs.tz()
            : false;

    useEffect(() => {
        if (specialniSluzby && !zmenaPozadavku) {
            setLoading(true);
            setLevel('specialniSluzby', {
                ...specialniSluzby,
                detailFiltrDLT: {
                    ...pagination
                },
                pozadavek: undefined
            });
            uePozadavkyDetailDLTApi(specialniSluzby?.detail?.id!, pagination)
                .then((resp: IPozadavkyDLT) => {
                    setLevel('specialniSluzby', {
                        ...specialniSluzby,
                        detail: {
                            ...specialniSluzby.detail,
                            ...resp,
                            dlt: true
                        },
                        detailFiltr: {
                            ...pagination
                        },
                        pozadavek: undefined
                    });
                    setData({
                        ...specialniSluzby.detail,
                        ...resp
                    });

                    form2.resetFields();
                })
                .catch((err: any) => {
                    //  err.response?.status === 404 && setData(undefined);
                    setErrStatus(err.response?.status);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination, zmenaPozadavku]);

    const columns = [
        {
            title: <TranslateText>CISLO_UCTU</TranslateText>,
            fixed: true,
            dataIndex: 'ucet',
            key: 'ucet',
            sorter: true
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            fixed: true,
            sorter: true
        },
        ...(specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI
            ? [
                  {
                      title: <TranslateText>CASTKA</TranslateText>,
                      dataIndex: 'castka',
                      key: 'castka',
                      fixed: true,
                      sorter: true
                  }
              ]
            : []),
        ...(!isZpetnyPrevod
            ? [
                  {
                      title: <TranslateText>CISLO_ZASTAVNI_SMLOUVY_ROW</TranslateText>,
                      dataIndex: 'cislozastavnismlouvy',
                      key: 'cislozastavnismlouvy',
                      sorter: true
                  }
              ]
            : []),
        {
            title: <TranslateText>STAV_POZADAVKU</TranslateText>,
            dataIndex: 'stav',
            key: 'stav',
            sorter: true,
            render: (text: string) => (
                <>
                    <TranslateText>
                        {text === 'U700' && specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI
                            ? 'SS_DETAIL_STAV_U700_ALT'
                            : 'SS_DETAIL_STAV_' + text}
                    </TranslateText>
                </>
            )
        },
        ...(!isZpetnyPrevod
            ? [
                  {
                      title: <TranslateText>UPOZORNENI</TranslateText>,
                      dataIndex: 'upozorneni',
                      key: 'upozorneni',
                      sorter: true
                  },
                  {
                      ellipsis: {
                          showTitle: false
                      },
                      key: 'sankce',
                      sorter: true,
                      width: 24,
                      dataIndex: 'sankce',
                      render: (sankce: boolean) =>
                          sankce ? (
                              <TooltipComponent title="SS_SANKCE_TOOLTIP">
                                  <p
                                      style={{
                                          color: 'red',
                                          fontWeight: 'bold',
                                          fontSize: 20,
                                          lineHeight: 0,
                                          position: 'relative',
                                          top: 9
                                      }}
                                  >
                                      !
                                  </p>
                              </TooltipComponent>
                          ) : (
                              <></>
                          )
                  },
                  {
                      title: <TranslateText>ZPUSOB_PORIZENI</TranslateText>,
                      dataIndex: 'zpusobporizeni',
                      key: 'zpusobporizeni',
                      sorter: true,
                      render: (text: string) => (
                          <>
                              <TranslateText>{'SS_DETAIL_TAB_PORIZENI_' + text}</TranslateText>
                          </>
                      )
                  },
                  {
                      title: <TranslateText>PODROBNOSTI</TranslateText>,
                      dataIndex: 'podrobnosti',
                      key: 'podrobnosti',
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      fixed: 'right' as any,
                      render: (_text: string, row: IPozadavkyPolozkaDLT, index: number) => (
                          <AnchorComponent
                              id={`zobrazitBtn${index}`}
                              onClick={() => {
                                  setSelectedDetail([row]);
                                  setLevel('specialniSluzby', {
                                      ...specialniSluzby,
                                      pozadavek: {
                                          ...row
                                      }
                                  });
                              }}
                          >
                              <TranslateText>ZOBRAZIT_DETAIL</TranslateText>
                          </AnchorComponent>
                      )
                  }
              ]
            : [])
    ];

    const columnsDetail = (
        <>
            {specialniSluzby?.detail?.isin && (
                <RowTitles
                    firstText="ISIN"
                    secondText={String(specialniSluzby?.detail.isin)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
            {specialniSluzby?.detail?.datum && (
                <RowTitles
                    firstText="DATUM"
                    secondText={String(specialniSluzby ? dayjs(specialniSluzby.detail.datum).format('DD.MM.YYYY') : '')}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
            {selectedDetail && selectedDetail[0].ucet && (
                <RowTitles
                    firstText="CISLO_UCTU"
                    secondText={String(selectedDetail && selectedDetail[0].ucet)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
            {selectedDetail && selectedDetail[0].pocetcp && (
                <RowTitles
                    firstText={'POCET_CP'}
                    secondText={String(selectedDetail && selectedDetail[0].pocetcp)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
            {selectedDetail && selectedDetail[0].cislozastavnismlouvy && (
                <RowTitles
                    firstText="CISLO_ZASTAVNI_SMLOUVY"
                    secondText={String(selectedDetail && selectedDetail[0].cislozastavnismlouvy)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
            {selectedDetail && selectedDetail[0].ppn && (
                <RowTitles
                    firstText="PPN_SELECT"
                    secondText={'ANO'}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
            {selectedDetail && selectedDetail[0].pocetcp && selectedDetail[0].cislozastavnismlouvy && (
                <RowTitles
                    firstText={'ZASTAVENE_CP'}
                    secondText={String(selectedDetail && selectedDetail[0].pocetcp)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
            {selectedDetail && selectedDetail[0].stav && (
                <RowTitles
                    firstText="STAV_POZADAVKU"
                    secondText={String(selectedDetail && 'SS_DETAIL_STAV_' + selectedDetail[0].stav)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
            {selectedDetail && selectedDetail[0].zpusobporizeni && (
                <RowTitles
                    firstText="ZPUSOB_PORIZENI"
                    secondText={String(
                        selectedDetail && selectedDetail[0].zpusobporizeni
                            ? 'SS_DETAIL_TAB_PORIZENI_' + selectedDetail[0].zpusobporizeni
                            : ''
                    )}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
            {selectedDetail && selectedDetail[0].upozorneni && (
                <RowTitles
                    firstText="UPOZORNENI"
                    secondText={String(selectedDetail && selectedDetail[0].upozorneni)}
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
            )}
        </>
    );

    const filterData = (values: any) => {
        setPagination(pv => {
            return Object.fromEntries(
                Object.entries({
                    ...pv,
                    ...values
                }).filter(([, v]) => v !== undefined && v !== '')
            ) as unknown as IDetailDLTinput;
        });
    };

    const renderButtons = () => {
        return (
            <Row className="button-row">
                <ButtonComponent
                    id="zpetBtn"
                    buttontext="ZPET"
                    type="default"
                    onClick={() => {
                        navigateWorkaround(
                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_PREHLED_AKTIVNICH_POZADAVKU}`
                        );
                    }}
                />
                {!isZpetnyPrevod && (
                    <>
                        {/* - po stisknutí tlačítka se zobrazí stránka Ruční vložení požadavku
                    - jen v případě, že lze zadávat další položky - aktuální datum je více než 3 dny před datem požadavku. 
                */}
                        {
                            // dayjs().diff(specialniSluzby?.detail.datum, 'd') < -2 &&
                            !isAfterMezniDatum &&
                                specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                                !specialniSluzby?.detail.puvodcdcp && (
                                    <ButtonComponent
                                        id="pridatPozadavekBtn"
                                        buttontext="SS_PRIDAT_POZADAVEK"
                                        onClick={() => {
                                            setZmenaPozadavku('novy');
                                        }}
                                    />
                                )
                        }
                        {/* - po stisknutí tlačítka se zobrazí stránka Vložení požadavku souborem
                -    jen v případě, že lze zadávat další položky - tzn. aktuální datum je více než 3 dny před datem požadavku. 
                */}
                        {
                            // dayjs().diff(specialniSluzby?.detail.datum, 'd') < -2 &&
                            !isAfterMezniDatum &&
                                specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                                !specialniSluzby?.detail.puvodcdcp && (
                                    <ButtonComponent
                                        id="pridatPozadavkeSouborBtn"
                                        buttontext="SS_PRIDAT_ZE_SOUBORU"
                                        onClick={() => {
                                            navigateWorkaround(
                                                `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_VLOZENI_SOUBOR}`
                                            );
                                        }}
                                    />
                                )
                        }

                        {/* - po stisknutí tlačítka se zobrazí stránka Seznam požadavků pro zaevidování
                    - tlačítko se zobrazí jen v případě, že v tabulce je alespoň jedna položka, která není zaevidovaná v CDCP
                */}
                        {/* {hasStavMensi(704) && ( */}
                        {data &&
                            !isAfterMezniDatum &&
                            (data?.zaevidovaneucty || 0) < (data?.pocetuctu || 0) &&
                            specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                            !specialniSluzby?.detail.puvodcdcp && (
                                <ButtonComponent
                                    id="zaevidovatBtn"
                                    buttontext="SS_ZAEVIDOVAT_V_CDCP"
                                    onClick={() => {
                                        setLevel('specialniSluzby', {
                                            ...specialniSluzby,
                                            detail: {
                                                ...specialniSluzby.detail,
                                                polozky: (specialniSluzby.detail?.polozky || []).filter(
                                                    e =>
                                                        e.stav !== 'U006' &&
                                                        (e.stav === 'U008'
                                                            ? e.lzeevidovatod
                                                                ? dayjs(e.lzeevidovatod) > dayjs()
                                                                : false
                                                            : e.stav === 'U007')
                                                )
                                            },
                                            seznamTyp: ETypSeznam.ZAEVIDOVANY
                                        });
                                        navigateWorkaround(
                                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_SEZNAM_DLT}`
                                        );
                                    }}
                                />
                            )}
                        {/* - po stisknutí tlačítka se spustí generování souboru (zobrazí se informace o stažení). 
                    Zavolá se metoda GET /UE/{id}/soubor/vystupcsv. Aktuální filtr na stránce bude posílaný v parametrech metody.
                */}
                        {data && data.count > 0 && !specialniSluzby?.detail?.puvodcdcp && (
                            <ButtonComponent
                                id="csvDownloadbtn"
                                buttontext="VYSTUP_CSV"
                                onClick={() => {
                                    if (specialniSluzby) {
                                        setLoading(true);
                                        ueSouborVystupCSV(specialniSluzby.detail?.id!, pagination, true)
                                            .then(file => {
                                                if (file.redirect) {
                                                    fileDownload(file.redirect).finally(() => setLoading(false));
                                                }
                                            })
                                            .catch((err: any) => {
                                                console.log('MHO GET CSV error', err);
                                            })
                                            .finally(() => {
                                                setLoading(false);
                                            });
                                    }
                                }}
                            />
                        )}
                        {/* - po stisknutí tlačítka se zobrazí stránka Seznam požadavků pro smazání
                    - tlačítko se zobrazí jen v případě, že pocetcpuspesnezaevidovano je menší než pocetcp
                */}
                        {
                            // (specialniSluzby?.detail.typ === 'SICAV'
                            //     ? data?.pocetcp + data?.pocetcpodkup
                            //     : data?.pocetcp) &&
                        }
                        {!!data?.count &&
                            !data?.zaevidovano &&
                            !isAfterMezniDatum &&
                            specialniSluzby?.detail?.stav === EStavPozadavku.OTEVRENO &&
                            !specialniSluzby?.detail.puvodcdcp && (
                                <ButtonComponent
                                    id="smazatBtn"
                                    buttontext="SMAZAT"
                                    onClick={() => {
                                        setLevel('specialniSluzby', {
                                            ...specialniSluzby,
                                            seznamTyp: ETypSeznam.SMAZANY
                                        });
                                        navigateWorkaround(
                                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_SEZNAM_DLT}`
                                        );
                                    }}
                                />
                            )}
                        {!data?.zaevidovano && !(data?.polozky || []).length && !isAfterMezniDatum && (
                            <ButtonComponent
                                buttontext="EDITOVAT_UPIS"
                                id="editBtn"
                                onClick={() => {
                                    navigateWorkaround(
                                        `${routePathEnum.FIRMA}${
                                            specialniSluzby?.detail?.typ === ETypPozadavku.UPIS
                                                ? routePathEnum.SLUZBY_NOVY_UPIS
                                                : specialniSluzby?.detail?.typ === ETypPozadavku.SICAV
                                                  ? routePathEnum.SLUZBY_NOVY_SICAV
                                                  : specialniSluzby?.detail?.typ === ETypPozadavku.PREVOD
                                                    ? routePathEnum.SLUZBY_NOVY_PREVOD
                                                    : specialniSluzby?.detail?.typ === ETypPozadavku.NAVYSENI
                                                      ? routePathEnum.SLUZBY_NOVY_NAVYSENI
                                                      : routePathEnum.SLUZBY_NOVY_UPIS_SICAV
                                        }`
                                    );
                                }}
                            />
                        )}
                        {data?.stavdlt === 'ERROR' && specialniSluzby?.detail?.stav !== EStavPozadavku.VYRIZENO && (
                            <ButtonComponent
                                buttontext="EDITOVAT_POTVRDIT"
                                id="potvrditbtn"
                                onClick={() => setShowConfirmationModal('potvrdit')}
                            />
                        )}
                        {data?.stavdlt === 'ERROR' &&
                            !specialniSluzby?.detail?.puvodcdcp &&
                            specialniSluzby?.detail?.stav !== EStavPozadavku.VYRIZENO && (
                                <ButtonComponent
                                    buttontext="EDITOVAT_ZRUSIT"
                                    id="editBtn"
                                    onClick={() => setShowConfirmationModal('zrusit')}
                                />
                            )}
                        {!data?.count && !specialniSluzby?.detail?.puvodcdcp && !isAfterMezniDatum && (
                            <ButtonComponent
                                buttontext="EDITOVAT_SMAZAT"
                                id="smazatBtn"
                                onClick={() => {
                                    confirm({
                                        title: 'Potvrzení',
                                        content: `Opravdu si přejete smazat ${
                                            getLanguageObject(language).SS_DETAIL_POZADAVEK
                                        } ${
                                            getLanguageObject(language)[
                                                'SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ
                                            ]
                                        } - ${getLanguageObject(language).ISIN}: ${specialniSluzby?.detail?.isin}, ${
                                            getLanguageObject(language).DATUM
                                        }: ${dayjs(specialniSluzby?.detail?.datum).format('DD.MM.YYYY')}`,
                                        cancelText: 'Ne',
                                        okText: 'Ano',
                                        onOk() {
                                            setLoading(true);
                                            ueZruseniDLT(specialniSluzby?.detail?.id!)
                                                .then(resp => {
                                                    if (resp.chybovystav) {
                                                        setErrModalMsg(`ZRUSENI_${resp.chybovystav}`);
                                                    } else {
                                                        navigateWorkaround(
                                                            // eslint-disable-next-line max-len
                                                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_PREHLED_AKTIVNICH_POZADAVKU}`
                                                        );
                                                    }
                                                })
                                                .finally(() => setLoading(false));
                                        }
                                    });
                                }}
                            />
                        )}
                    </>
                )}
            </Row>
        );
    };

    const renderSelectedDetail = () => {
        return (
            <Modal
                open={!!selectedDetail}
                onCancel={() => setSelectedDetail(undefined)}
                title={
                    <>
                        <TranslateText>SS_DETAIL_VYBRANY</TranslateText>{' '}
                        <TranslateText>{'SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ}</TranslateText>
                    </>
                }
                footer={[
                    <Row className="button-row-modal">
                        {selectedDetail &&
                            !specialniSluzby?.detail?.puvodcdcp &&
                            ['U001', 'U002', 'U003', 'U006', 'U007', 'U008', 'U700', 'U701', 'U770'].includes(
                                selectedDetail[0].stav || ''
                            ) && (
                                <ButtonComponent
                                    id="smazatVybranyBtn"
                                    buttontext="SS_DETAIL_VYBRANY_SMAZAT"
                                    disabled={selectedDetail[0].sankce}
                                    onClick={() => {
                                        setLevel('specialniSluzby', {
                                            ...specialniSluzby,
                                            pozadavek: {
                                                ...selectedDetail[0]
                                            },
                                            seznamTyp: ETypSeznam.SMAZANY
                                        });
                                        navigateWorkaround(
                                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_SEZNAM_DLT}`
                                        );
                                    }}
                                />
                            )}
                        {selectedDetail &&
                            !specialniSluzby?.detail?.puvodcdcp &&
                            ['U001', 'U002', 'U003', 'U006', 'U007', 'U008'].includes(selectedDetail[0].stav || '') && (
                                <ButtonComponent
                                    id="zmenitVybranyBtn"
                                    buttontext="SS_DETAIL_VYBRANY_ZMENIT"
                                    disabled={selectedDetail[0].sankce}
                                    onClick={() => {
                                        setLevel('specialniSluzby', {
                                            ...specialniSluzby,
                                            pozadavek: {
                                                ...selectedDetail[0]
                                            },
                                            updateDetailu: true
                                        });
                                        setZmenaPozadavku('edit');
                                    }}
                                />
                            )}
                    </Row>
                ]}
            >
                {columnsDetail}
            </Modal>
        );
    };

    const renderConfirmation = () => {
        return (
            <Modal
                width={700}
                open={!!showConfirmationModal}
                onCancel={() => setShowConfirmationModal(undefined)}
                title={
                    <>
                        <TranslateText>SS_DETAIL_VYBRANY</TranslateText>{' '}
                        <TranslateText>{'SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ}</TranslateText>
                    </>
                }
                footer={[
                    <Row className="button-row">
                        <ButtonComponent
                            id="modalZpetBtn"
                            buttontext="ZPET"
                            type="default"
                            onClick={() => setShowConfirmationModal(undefined)}
                        />

                        <ButtonComponent
                            id="potvrditZrusitBtn"
                            buttontext={showConfirmationModal === 'potvrdit' ? 'EDITOVAT_POTVRDIT' : 'EDITOVAT_ZRUSIT'}
                            onClick={() => {
                                const showNotificationFinish = (msg?: string) => {
                                    const title =
                                        msg === 'OK'
                                            ? showConfirmationModal === 'potvrdit'
                                                ? 'POTVRZENI_UPISU'
                                                : 'ZRUSENI_UPISU'
                                            : `POTVRZENI_${msg}`;
                                    const text =
                                        msg === 'OK'
                                            ? showConfirmationModal === 'potvrdit'
                                                ? 'POTVRZENI_DLT_SUCCESS'
                                                : 'ZRUSENI_DLT_SUCCESS'
                                            : showConfirmationModal === 'potvrdit'
                                              ? 'POTVRZENI_DLT_ERR'
                                              : 'ZRUSENI_DLT_ERR';
                                    return NotificationComponent({
                                        title: title,
                                        description: text,
                                        type: msg === 'OK' ? ENotificationType.SUCCESS : ENotificationType.ERROR
                                    });
                                };

                                setLoading(true);
                                if (showConfirmationModal === 'potvrdit' && data?.id) {
                                    uePotvrzeniSingleDLT(data.id)
                                        .then(resp => {
                                            setPagination(pv => {
                                                return {
                                                    ...pv
                                                };
                                            });
                                            showNotificationFinish(resp.chybovystav ? resp.chybovystav : 'OK');
                                            if (!resp.chybovystav || resp.chybovystav === 'OK')
                                                setShowConfirmationModal(undefined);
                                        })
                                        .finally(() => setLoading(false));
                                } else if (data?.id)
                                    ueZruseniSingleDLT(data.id)
                                        .then(resp => {
                                            setPagination(pv => {
                                                return {
                                                    ...pv
                                                };
                                            });
                                            showNotificationFinish(resp.chybovystav ? resp.chybovystav : 'OK');
                                            if (!resp.chybovystav || resp.chybovystav === 'OK')
                                                setShowConfirmationModal(undefined);
                                        })
                                        .finally(() => setLoading(false));
                            }}
                        />
                    </Row>
                ]}
            >
                <Title
                    level={5}
                    text={showConfirmationModal === 'potvrdit' ? 'POTVRZENI_DLT_TEXT_TOP' : 'ZRUSENI_DLT_TEXT_TOP'}
                />

                <RowTitles
                    firstText="AKTUALNI_STAV_UPISU"
                    secondText={specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI ? 'CP' : undefined}
                    thirdText={
                        specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI
                            ? specialniSluzby.detail.mena || 'CZK'
                            : undefined
                    }
                    style={rowTitleStyles}
                    span={12}
                    bold={true}
                    paragraph={true}
                />
                <RowTitles
                    firstText="OBJEM_EMISE"
                    secondText={formatInputNumberThousand(String(data?.pocetcpupisu))}
                    thirdText={
                        specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI
                            ? formatInputNumberThousand(
                                  String(((data?.pocetcpupisu || 0) * (data?.cenacp || 0)).toFixed(2))
                              )
                            : undefined
                    }
                    paragraph={true}
                    style={rowTitleStyles}
                    span={12}
                />
                {specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI && (
                    <>
                        <RowTitles
                            firstText="MIN_POCET_CP_SHORT"
                            bold={true}
                            secondText={formatInputNumberThousand(String(data?.minpoccp))}
                            thirdText={formatInputNumberThousand(
                                String(((data?.minpoccp || 0) * (data?.cenacp || 0)).toFixed(2))
                            )}
                            paragraph={true}
                            style={rowTitleStyles}
                            span={12}
                        />
                        <RowTitles
                            firstText="UHRAZENE_CP"
                            bold={true}
                            secondText={formatInputNumberThousand(
                                String((data?.pocetcp || 0) - (data?.neuhrazenecp || 0))
                            )}
                            thirdText={formatInputNumberThousand(
                                String(
                                    (((data?.pocetcp || 0) - (data?.neuhrazenecp || 0)) * (data?.cenacp || 0)).toFixed(
                                        2
                                    )
                                )
                            )}
                            paragraph={true}
                            style={rowTitleStyles}
                            span={12}
                        />
                        <RowTitles
                            firstText="NEUHRAZENE_CP"
                            secondText={formatInputNumberThousand(String(data?.neuhrazenecp))}
                            thirdText={formatInputNumberThousand(
                                String(((data?.neuhrazenecp || 0) * (data?.cenacp || 0)).toFixed(2))
                            )}
                            paragraph={true}
                            style={rowTitleStyles}
                            span={12}
                        />
                    </>
                )}
                {specialniSluzby?.detail?.typprevodu === ETypPrevodu.BEZZAPLACENI && (
                    <RowTitles
                        firstText="ZAEVIDOVANE_CP"
                        secondText={String(data?.zaevidovanecp)}
                        paragraph={true}
                        style={rowTitleStyles}
                        span={12}
                    />
                )}
                {specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI && data?.cenacp && (
                    <RowTitles
                        firstText="CENA_CP"
                        secondText={' '}
                        thirdText={formatInputNumberThousand(String(data?.cenacp || 0))}
                        paragraph={true}
                        style={rowTitleStyles}
                        span={12}
                    />
                )}
                <Paragraph
                    style={{
                        display: 'block',
                        marginTop: 20,
                        fontWeight: 'bold'
                    }}
                    text={
                        showConfirmationModal === 'zrusit'
                            ? `${getLanguageObject(language).ZRUSENI_DLT_TEXT} ${formatInputNumberThousand(
                                  (data?.pocetcp || 0) * (data?.cenacp || 0) + ' ' + data?.mena
                              )}.`
                            : `${getLanguageObject(language).POTVRZENI_DLT_TEXT}${
                                  formatInputNumberThousand(data?.pocetcp || 0) + ' CP'
                              }${
                                  specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI
                                      ? ', ' +
                                        formatInputNumberThousand((data?.pocetcp || 0) * (data?.cenacp || 0)) +
                                        ' ' +
                                        data?.mena
                                      : ''
                              }.`
                    }
                />
            </Modal>
        );
    };

    return (
        <LoadingWrapper loading={loading}>
            {specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI ? (
                <Collapse expandIconPosition="right" bordered={false} className="titleCollapse">
                    <Panel
                        header={
                            <Title
                                level={4}
                                text={`${
                                    getLanguageObject(language)[
                                        zmenaPozadavku === 'novy' ? 'SS_NOVY_POZADAVEK_NA' : 'SS_DETAIL_POZADAVEK'
                                    ]
                                } ${
                                    getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail.typ]
                                } - ${getLanguageObject(language).ISIN}: ${specialniSluzby?.detail.isin}, 
                            ${getLanguageObject(language).DATUM}: ${dayjs(specialniSluzby?.detail.datum).format(
                                'DD.MM.YYYY'
                            )}${
                                specialniSluzby.detail.typprevodu
                                    ? ', ' +
                                      getLanguageObject(language)['TYP_PREVODU_' + specialniSluzby.detail.typprevodu]
                                    : ''
                            }`}
                            />
                        }
                        key="detail"
                    >
                        <div
                            style={{
                                width: 790
                            }}
                        >
                            {(data?.meznidatum || specialniSluzby?.detail.meznidatum) && (
                                <RowTitles
                                    firstText="LZE_ODESLAT"
                                    secondText={String(
                                        dayjs(data?.meznidatum || specialniSluzby?.detail.meznidatum).format(
                                            'DD.MM.YYYY'
                                        ) + ' 16:30'
                                    )}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            <RowTitles
                                firstText="DATUN_UHRADY_LATEST"
                                secondText={String(
                                    data?.datuhr
                                        ? dayjs(data.datuhr).format('DD.MM.YYYY HH:mm')
                                        : specialniSluzby?.detail.datuhr
                                          ? dayjs(specialniSluzby?.detail.datuhr).format('DD.MM.YYYY HH:mm')
                                          : ''
                                )}
                                paragraph={true}
                                style={rowTitleStylesCollapse}
                                span={14}
                            />
                            {data?.cenacp && (
                                <RowTitles
                                    firstText="CENA_CP"
                                    secondText={formatInputNumberThousand(String(data ? data.cenacp : 0))}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            <RowTitles
                                firstText="MENA"
                                secondText={String(data ? data.mena : '')}
                                paragraph={true}
                                style={rowTitleStylesCollapse}
                                span={14}
                            />
                            {data?.bankucet && (
                                <RowTitles
                                    firstText="VYPLATA_BANKUC"
                                    secondText={String(data ? data.bankucet : '')}
                                    paragraph={true}
                                    style={rowTitleStylesCollapse}
                                    span={14}
                                />
                            )}
                            <RowTitles
                                firstText="PREDPOKLADANY_POCET_CP"
                                secondText={formatInputNumberThousand(String(data ? data.pocetcpupisu : 0))}
                                paragraph={true}
                                style={rowTitleStylesCollapse}
                                span={14}
                            />
                            <RowTitles
                                firstText="MIN_POCET_CP"
                                secondText={formatInputNumberThousand(String(data ? data.minpoccp : 0))}
                                paragraph={true}
                                style={rowTitleStylesCollapse}
                                span={14}
                            />
                        </div>
                    </Panel>
                </Collapse>
            ) : (
                <Title
                    level={4}
                    text={`${
                        isZpetnyPrevod
                            ? ''
                            : getLanguageObject(language)[
                                  zmenaPozadavku === 'novy' ? 'SS_NOVY_POZADAVEK_NA' : 'SS_DETAIL_POZADAVEK'
                              ]
                    } ${getLanguageObject(language)[isZpetnyPrevod ? 'SS_PREHLED_POZADAVEK_PREVODZPET' : 'SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ]} - ${
                        getLanguageObject(language).ISIN
                    }: ${specialniSluzby?.detail?.isin}, 
                            ${getLanguageObject(language).DATUM}: ${dayjs(specialniSluzby?.detail?.datum).format(
                                'DD.MM.YYYY'
                            )}${
                                specialniSluzby?.detail?.typprevodu
                                    ? ', ' +
                                      getLanguageObject(language)['TYP_PREVODU_' + specialniSluzby?.detail.typprevodu]
                                    : ''
                            }`}
                />
            )}

            {errStatus && errStatus !== 404 ? (
                <Row>
                    <Col span={16}>
                        {errStatus === 404 ? (
                            <Paragraph text="RESPONSE_ERROR_404" />
                        ) : errStatus ? (
                            <Paragraph text="SLUZBA_NEDOSTUPNA" />
                        ) : null}
                        <Row className="button-row">
                            <ButtonComponent
                                id="errZpetBtn"
                                type="default"
                                buttontext="ZPET"
                                onClick={() => {
                                    // navigateWorkaround(
                                    //     // eslint-disable-next-line max-len
                                    //     `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_PREHLED_AKTIVNICH_POZADAVKU}`
                                    // );
                                    navigateWorkaround(-1);
                                }}
                            />
                        </Row>
                    </Col>
                </Row>
            ) : zmenaPozadavku ? (
                <NovyPozadavekDLT
                    setZmenaPozadavku={setZmenaPozadavku}
                    data={data}
                    detailData={selectedDetail && selectedDetail[0]}
                    setSelectedDetail={setSelectedDetail}
                    zmenaPozadavku={zmenaPozadavku}
                />
            ) : (
                <>
                    <FormWrapper onFinish={filterData} outForm={form}>
                        <Row gutter={8} /* align="bottom"*/>
                            <Col span={isZpetnyPrevod ? 7 : 3}>
                                <InputComponent name="ucet" label="CISLO_UCTU" notRequired={true} />
                            </Col>
                            <Col span={3}>
                                <InputComponent name="pocetcp" label="POCET_CP" notRequired={true} />
                            </Col>
                            {!isZpetnyPrevod && (
                                <Col span={3}>
                                    <InputComponent
                                        name="cislozastavnismlouvy"
                                        label="CISLO_ZASTAVNI_SMLOUVY"
                                        maxLength={10}
                                        notRequired={true}
                                    />
                                </Col>
                            )}
                            <Col span={isZpetnyPrevod ? 6 : 4}>
                                <SelectComponent
                                    isFormItem={true}
                                    data={typStavZadostiDLTSelect}
                                    name="stav"
                                    labelParagraph="STAV_POZADAVKU"
                                    notRequired={true}
                                    showTooltipValue={true}
                                />
                            </Col>
                            {!isZpetnyPrevod && (
                                <>
                                    {specialniSluzby?.detail?.sankce && (
                                        <Col span={2}>
                                            <SelectComponent
                                                labelParagraph="SANKCE"
                                                allowClear={true}
                                                isFormItem={true}
                                                showTooltipValue={true}
                                                notRequired={true}
                                                name="sankce"
                                                data={identifikaceOsobySelect}
                                            />
                                        </Col>
                                    )}
                                    <Col span={isZpetnyPrevod ? 4 : 3}>
                                        <SelectComponent
                                            name="zpusobpor"
                                            labelParagraph="ZPUSOB_PORIZENI"
                                            notRequired={true}
                                            allowClear={true}
                                            isFormItem={true}
                                            showTooltipValue={true}
                                            data={typZpusobPorizeniSelect}
                                        />
                                    </Col>
                                </>
                            )}
                        </Row>
                        <Row
                            className="button-row-modal"
                            gutter={12}
                            style={{
                                position: 'relative',
                                top: -12
                            }}
                        >
                            <ButtonComponent id="filtrSubmitBtn" buttontext="FILTROVAT" onClick={() => form.submit()} />
                            <ButtonComponent
                                id="filterClearBtn"
                                buttontext="VSECHNY"
                                onClick={() => {
                                    form.resetFields();
                                    setPagination({
                                        offset: 0,
                                        limit: 10,
                                        order: 'poradi:desc:idpolozky:desc'
                                    });
                                }}
                            />
                        </Row>
                    </FormWrapper>

                    <Table
                        rowKey={(_, index) => index || 0}
                        columns={columns}
                        scroll={{
                            x: true
                        }}
                        dataSource={data?.polozky || []}
                        locale={{
                            emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                        }}
                        pagination={{
                            total: data?.count || 0,
                            onChange: page => {
                                setPagination({
                                    offset: (page - 1) * pagination.limit,
                                    limit: pagination.limit,
                                    order: pagination.order
                                });
                            },
                            current:
                                pagination?.limit && pagination?.offset
                                    ? Math.ceil((pagination?.offset + 1) / pagination?.limit)
                                    : 1,
                            showSizeChanger: true,
                            defaultPageSize: 10
                        }}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onChange={(paginat, _b, sorter: any) =>
                            setPagination(pv => {
                                return {
                                    ...pv,
                                    ...(sorter?.order && {
                                        order: `${sorter.columnKey}:${
                                            sorter?.order === 'descend' ? 'desc' : 'asc'
                                        }:idpolozky:desc`
                                    }),
                                    limit: paginat.pageSize || 10
                                };
                            })
                        }
                    />
                    {data?.count === 0 && isZpetnyPrevod && (
                        <Title
                            level={5}
                            text="SS_PREVODZPET_404"
                            style={{
                                marginTop: 20,
                                marginBottom: 20
                            }}
                        />
                    )}
                    <FormWrapper onFinish={() => {}} outForm={form2}>
                        <Row gutter={8}>
                            {data?.pocetcp ? (
                                <Col>
                                    <InputComponent
                                        name="pocetcp"
                                        label="POCET_CP"
                                        notRequired={true}
                                        defaultValue={data.pocetcp}
                                        disabled={true}
                                    />
                                </Col>
                            ) : null}
                            {data?.zaevidovanecp ? (
                                <Col>
                                    <InputComponent
                                        name="zaevidovanecp"
                                        label="ZAEVIDOVANE_CP"
                                        notRequired={true}
                                        defaultValue={data.zaevidovanecp}
                                        disabled={true}
                                    />
                                </Col>
                            ) : null}
                            {data?.pocetuctu ? (
                                <Col>
                                    <InputComponent
                                        name="pocetuctu"
                                        label="SS_PREHLED_POCET_UCTU"
                                        notRequired={true}
                                        defaultValue={data.pocetuctu}
                                        disabled={true}
                                    />
                                </Col>
                            ) : null}
                            {specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI && (
                                <>
                                    {data?.minpoccp ? (
                                        <Col>
                                            <InputComponent
                                                name="minpoccp"
                                                label="MIN_POCET_CP_SHORT"
                                                notRequired={true}
                                                defaultValue={data.minpoccp}
                                                disabled={true}
                                            />
                                        </Col>
                                    ) : null}
                                    {data?.zaevidovanecp ? (
                                        <Col>
                                            <InputComponent
                                                name="neuhrazenecp"
                                                label="UHRAZENE_CP"
                                                notRequired={true}
                                                defaultValue={data.zaevidovanecp - data.neuhrazenecp}
                                                disabled={true}
                                            />
                                        </Col>
                                    ) : null}
                                    {data?.neuhrazenecp ? (
                                        <Col>
                                            <InputComponent
                                                name="neuhrazenecp"
                                                label="NEUHRAZENE_CP"
                                                notRequired={true}
                                                defaultValue={data.neuhrazenecp}
                                                disabled={true}
                                            />
                                        </Col>
                                    ) : null}
                                    {data?.neuhrazeneucty ? (
                                        <Col>
                                            <InputComponent
                                                name="neuhrazeneucty"
                                                label="NEUHRAZENE_UCTY"
                                                notRequired={true}
                                                defaultValue={data.neuhrazeneucty}
                                                disabled={true}
                                            />
                                        </Col>
                                    ) : null}
                                </>
                            )}
                        </Row>
                    </FormWrapper>
                    {renderButtons()}
                    {renderSelectedDetail()}
                    {renderConfirmation()}
                    <ErrModal modalMsg={errModalMsg} setModalMsg={setErrModalMsg} />
                </>
            )}
        </LoadingWrapper>
    );
};

export default DetailPozadavkuDLT;
